import { createSlice } from "@reduxjs/toolkit";
import {
  clearStoredAuth,
  persistAuth,
  persistTokens,
  readAuthFromStorage,
} from "../../lib/auth";
import { normalizePermissionLevels } from "../../lib/permissions";

/*
 * The saved sign-in.
 *
 * `token` and `refreshToken` are the session. `profile` and `permissions` are
 * a CACHE of what the server last said about this admin: shown at once so the
 * dashboard opens without a round trip, and replaced by the server's answer on
 * every boot (auth/actions.js, loadSession). The server is the truth for who
 * is signed in and what they may see; the browser copy never is.
 *
 * `sessionStatus` says where that answer stands:
 *   idle     nothing asked yet (boot, or after sign-out)
 *   loading  asked, waiting
 *   ready    the state holds what the server said (a sign-in answer counts)
 *   failed   the server could not be reached; the cache, if any, stands
 */
const hydrateInitialState = () => {
  const snapshot = readAuthFromStorage();
  return {
    token: snapshot.token,
    refreshToken: snapshot.refreshToken,
    profile: snapshot.profile,
    permissions: snapshot.permissions,
    sessionStatus: "idle",
  };
};

const normalizePermissions = (raw) =>
  (Array.isArray(raw) ? raw : []).map((p) => ({
    ...p,
    menu_id: Number(p.menu_id),
    permission_level: normalizePermissionLevels(p.permission_level),
  }));

const authSlice = createSlice({
  name: "auth",
  initialState: hydrateInitialState(),
  reducers: {
    setAuth: (state, action) => {
      const { token, refreshToken, profile, permissions } = action.payload;
      state.token = token ?? state.token;
      state.refreshToken = refreshToken ?? state.refreshToken;
      state.profile = profile ?? state.profile;
      state.permissions = permissions
        ? normalizePermissions(permissions)
        : state.permissions;
      if (profile) state.sessionStatus = "ready";
      persistAuth({
        token: state.token,
        refreshToken: state.refreshToken,
        profile: state.profile,
        permissions: state.permissions,
      });
    },
    setTokens: (state, action) => {
      const { token, refreshToken } = action.payload;
      if (token) state.token = token;
      if (refreshToken) state.refreshToken = refreshToken;
      persistTokens({ token, refreshToken });
    },
    setProfile: (state, action) => {
      const incoming = action.payload;
      if (!incoming) {
        state.profile = null;
        return;
      }
      // Merge rather than replace. A self "My profile" save returns only the
      // edited fields; replacing wholesale would drop keys the response omits
      // (notably `is_super_admin`). A super-admin has no per-menu permission
      // records, so losing that flag silently blanks the entire sidebar and
      // persists the broken state until the next login.
      state.profile = { ...state.profile, ...incoming };
      persistAuth({ profile: state.profile });
    },
    sessionLoading: (state) => {
      state.sessionStatus = "loading";
    },
    sessionFailed: (state) => {
      state.sessionStatus = "failed";
    },
    clearAuth: (state) => {
      state.token = null;
      state.refreshToken = null;
      state.profile = null;
      state.permissions = [];
      state.sessionStatus = "idle";
      clearStoredAuth();
    },
  },
});

export const {
  setAuth,
  setTokens,
  setProfile,
  sessionLoading,
  sessionFailed,
  clearAuth,
} = authSlice.actions;
export default authSlice.reducer;
